import React from 'react';
import { useTranslation } from "react-i18next";
import HeroSection from '../../components/sections/HeroSection';
import BigTinyIntroSection from '../../components/sections/BigTinyIntroSection';
import OnePlusTwoGallery from '../../components/sections/OnePlusTwoGallery';
import FaqAccordionsSection from '../../components/sections/FaqAccordionsSection';
import FactsAndStatsSection from '../../components/sections/FactsAndStatsSection';
import PracticalInfoSection from '../../components/sections/PracticalInfoSection';
import ArticleWithModal from '../../components/sections/ArticleWithModal';
import VisaServicesPromotion from '../../components/sections/VisaServicesPromotion';
import FooterSection from '../../components/sections/FooterSection';
import DividersInfoSection from '../../components/sections/DividersInfoSection';
import ParallaxCallSection from '../../components/sections/ParallaxCallSection';

function AlmatyCityPage(props) {

    var { t } = useTranslation();

    var faqItems = [
        { question: "What documents are required?", answer: "Passport valid for at least 6 months, visa application form, passport photos, proof of accommodation, and travel insurance are standard requirements." },
        { question: "How long does processing take?", answer: "Standard processing usually takes up to 5 working days, while express processing can be completed in 48 hours depending on consular workload." },
        { question: "Can business delegations apply?", answer: "Yes, corporate visa packages are available for delegations, including bulk submissions, account-based invoicing, and consolidated document delivery." },
        { question: "Is visa on arrival available?", answer: "Visa on arrival is limited to specific cases. Pre-application ensures guaranteed entry and avoids administrative delays at the border." }
    ];

    return (
        <>
            <HeroSection
                isExpectingAnimations={true}
                title="Almaty - Economic Heart of Kazakhstan"
                paragraph="Almaty, formerly known as Alma-Ata, is the largest city in Kazakhstan and serves as its cultural and economic hub of the southern region part of the country"
                callToAction="Explore Almaty with Jai!"
                backgroundImageUrl="https://img.forbes.kz/forbes-photobank/media/2024-11-06/b0997f50-0cf5-4baf-aaf3-59eeadd7093c.webp"
            />
            <div className='uk-container'>

                <BigTinyIntroSection
                    centered={false}
                    Big="As Kazakhstan's financial and cultural capital, Almaty is the premier destination for professionals seeking dynamic careers in a cosmopolitan setting. Jai simplifies the visa and work permit process, enabling you to seize opportunities in finance, IT, and creative industries while enjoying a high-quality urban life."
                    Tiny="Beyond its business landscape, Almaty is a city of green parks, mountain trails, and a thriving arts scene. With Jai managing your relocation logistics, you can confidently immerse yourself in the city's vibrant culture, explore its stunning natural surroundings, and achieve a perfect work-life balance."
                />

                <section className="uk-grid-large uk-child-width-1-2@s uk-flex-middle" data-uk-grid>
                    <div>
                        <h1 className="uk-heading-medium uk-text-bold uk-margin-remove">
                            {t('Almaty — The Cultural & Business Capital')}
                        </h1>

                        <p className="uk-text-lead uk-margin-small-top">
                            Nestled at the foot of the majestic Tien Shan mountains, Almaty is Kazakhstan's largest metropolis. It serves as the nation's primary center for finance, technology, education, and tourism, blending a rich Soviet-era heritage with rapid modern development.
                        </p>

                        <p>
                            Once the capital, Almaty remains the country's vibrant soul, characterized by its wide, tree-lined avenues, numerous parks, and a sophisticated café culture. The city is a hub for international business, hosting the headquarters of major banks, multinational corporations, and a burgeoning startup ecosystem. Its cosmopolitan atmosphere is complemented by a rich cultural life, with world-class theaters, museums, and art galleries.
                        </p>

                        <p>
                            The surrounding region offers unparalleled recreational opportunities, from skiing at Shymbulak resort to hiking in the Ile-Alatau National Park. This unique combination of urban dynamism and natural beauty makes Almaty a highly attractive destination for both skilled professionals and international visitors.
                        </p>

                        <VisaServicesPromotion />
                    </div>

                    <OnePlusTwoGallery
                        BigImageLink="https://img.bes.media/besmedia-photobank/media/2024-05-22/830dd3ae-8d57-4527-a216-355c881d7637.webp"
                        BigImageDescription="Almaty cityscape with mountains"
                        SmallImage1Link="https://meteor-almaty.e-orda.kz/img/cachedImages/full/23053-3-2.jpg"
                        SmallImage1Description="Shymbulak ski resort"
                        SmallImage2Link="https://www.advantour.com/img/kazakhstan/almaty/zenkov-cathedral.jpg"
                        SmallImage2Description="Zenkov Cathedral"
                    />

                </section>
            </div>

            <ParallaxCallSection
                URL="https://sxodim.com/uploads/posts/2023/05/30/optimized/069f3e4342030be875b5a2f6dfff31ec_1522x570-q-85.jpg"
                headline="Find Inspiration in Almaty"
                callToAction="Let Jai handle your visa formalities while you plan your journey to Kazakhstan’s cultural and mountain capital — vibrant, green, and full of life."
            />

            <div className='uk-container'>
                <section className="uk-grid-large uk-child-width-1-3@m uk-margin-large-top" data-uk-grid>

                    <ArticleWithModal
                        Image="https://avatars.mds.yandex.net/get-altay/9847279/2a0000018dff6fe93744b3c32c454e1a219f/L_height"
                        ImageDescription="Almaty financial district"
                        ArticleTitle="Finance & Business"
                        Paragraph1="Almaty is the undisputed financial heart of Central Asia, hosting the Kazakhstan Stock Exchange (KASE), the National Bank, and the headquarters of the country's largest commercial banks. The city is a magnet for multinational corporations, financial institutions, and investment funds."
                        Paragraph2="Opportunities for foreign professionals are abundant in investment banking, corporate finance, asset management, and FinTech. The city's burgeoning startup scene also creates demand for venture capital specialists, business analysts, and tech entrepreneurs. The Almaty International Financial Centre (AIFC) further enhances its appeal by offering a common-law jurisdiction and preferential tax regimes."
                        Paragraph3="Professionals in auditing, compliance, and risk management are highly sought after to help local companies align with international standards. With its sophisticated business infrastructure and global connectivity, Almaty provides a fertile ground for ambitious careers in finance and commerce."
                        modalName="modal-business1"
                    />

                    <ArticleWithModal
                        Image="https://static.tengrinews.kz/userdata/news/2018/news_342441/thumb_m/photo_246517.jpg"
                        ImageDescription="Big Almaty Lake"
                        ArticleTitle="Tourism & Natural Beauty"
                        Paragraph1="Almaty's stunning natural setting makes it a premier destination for tourism and recreation. The city is nestled at the foot of the Trans-Ili Alatau mountains, offering easy access to world-class outdoor activities year-round."
                        Paragraph2="In winter, the Shymbulak ski resort attracts skiers and snowboarders from across the globe. During the summer, the mountains become a paradise for hikers, climbers, and nature lovers, with iconic destinations like Big Almaty Lake and the Charyn Canyon drawing thousands of visitors. The city's proximity to pristine nature is a key part of its unique lifestyle appeal."
                        Paragraph3="This thriving tourism sector creates opportunities in hospitality management, tour operations, and eco-tourism development. The city's blend of urban sophistication and natural splendor makes it an unforgettable place to live, work, and explore."
                        modalName="modal-business2"
                    />

                    <PracticalInfoSection
                        sectionTitle="Practical Information"
                        currencyInfo="Local currency: Kazakhstani tenge (KZT)."
                        airportInfo="Primary airport: Almaty International Airport (ALA), major international hub."
                        climateInfo="Climate: humid continental; warm summers, cold and snowy winters."
                        businessHoursInfo="Business hours: corporate offices typically 09:00–18:00; retail hours are longer and include weekends."
                    />
                </section>
            </div>

            {/* Quick Facts / Statistics */}
            <FactsAndStatsSection
                sectionTitle="Quick Facts"
                stat1="90%"
                fact1="Visa approval rate"
                stat2="635+"
                fact2="Clients served"
                stat3="14"
                fact3="Days processing time"
                stat4="1854"
                fact4="Year city founded"
            />

            <div className="uk-section uk-container">
                <div className="uk-text-center">
                    <h2 className="uk-h2 uk-text-bold">A Hub for Professionals and Creatives</h2>
                    <p className="uk-text-lead">
                        As the economic engine of Kazakhstan, Almaty boasts a diverse and sophisticated job market, attracting top talent from across the globe in finance, technology, marketing, and the arts.
                    </p>
                </div>

                <div className="uk-child-width-1-3@m uk-grid-match uk-margin-medium-top" data-uk-grid>
                    <div>
                        <div className="uk-card uk-card-body uk-card-hover uk-card-default">
                            <div className="uk-card-badge uk-label">High Demand</div>
                            <h3 className="uk-card-title"><span data-uk-icon="icon: future; ratio: 1.2" className="uk-margin-small-right"></span>Finance, FinTech & Banking</h3>
                            <p>Home to the Kazakhstan Stock Exchange and major banks, Almaty is the undisputed financial center. Opportunities are plentiful for investment analysts, fintech developers, risk managers, and compliance specialists with international experience.</p>
                        </div>
                    </div>
                    <div>
                        <div className="uk-card uk-card-body uk-card-hover uk-card-default">
                            <h3 className="uk-card-title"><span data-uk-icon="icon: code; ratio: 1.2" className="uk-margin-small-right"></span>IT, Marketing & Creative Industries</h3>
                            <p>A thriving tech scene and the presence of multinational HQs create strong demand for software engineers, digital marketers, brand managers, and graphic designers. The city's vibrant culture also supports a growing creative economy.</p>
                        </div>
                    </div>
                    <div>
                        <div className="uk-card uk-card-body uk-card-hover uk-card-default">
                            <h3 className="uk-card-title"><span data-uk-icon="icon: world; ratio: 1.2" className="uk-margin-small-right"></span>Hospitality, Tourism & Services</h3>
                            <p>As a major tourist destination, Almaty's hospitality sector is booming. There is a consistent need for experienced hotel managers, tourism development experts, and professionals in high-end retail and customer service to cater to an international clientele.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* FAQ Accordion */}
            <FaqAccordionsSection
                items={faqItems}
            />

            <div className='uk-container'>
                {/* Infosection with shitloads of dividers */}
                <DividersInfoSection />
            </div>

            <FooterSection />
        </>
    );
}

export default AlmatyCityPage;
